/**
 * External dependencies.
 */
import type { GetStaticProps, NextPage } from "next";
import Breadcrumbs from 'blocks/breadcrumbs/breadcrumbs';
import FormTopicSearch from 'blocks/form-topic-search/form-topic-search';
import HreflangControlled from 'blocks/hreflang/hreflang-controlled';
import Link from 'components/link/link';
import SchemaSet from 'components/schema-set/schema-set';
import SEO from 'components/seo/seo';
import TealBox from 'components/teal-box/teal-box';
import { useMemo } from 'react';
/**
 * Internal dependencies.
 */
import {
    fetchAPI, getLocale, getLocalizationData, getSlug, handleBaseLocales, loadTranslation
} from 'utils';

import { t, Trans } from '@lingui/macro';

import Layout from '../../../layout/layout';

const Documentation: NextPage = ({
  data,
  locale,
  locales,
  docSEO,
  categories,
  helpData,
  apps,
}: any) => {
 
  const firstDocsData = [...categories[0].attributes.documentations.data];
  function getFirstDocSublink(slug: string, type = "themes") {
    let array = firstDocsData.filter((doc: any) => {
      return (
        doc.attributes[type] &&
        doc.attributes[type].data.find(
          (theme: any) => theme.attributes.slug === slug
        )
      );
    });

    array.map((doc: any) => {
      if (doc.attributes.order === null)
        return (doc.attributes.order = array.length + 1);

      return doc.attributes.order;
    });

    array.sort((a: any, b: any) => a.attributes.order - b.attributes.order);
    return (
      array[0]?.attributes.slug_overwrite || array[0]?.attributes.slug || null
    );
  }

  return (
    <Layout
      title="Documentations"
      desc="Documentations"
      light
      notice
      hreflang={false}
    >
      <HreflangControlled localizations={locales} />
      <SEO data={docSEO?.attributes.SEO} />
      <SchemaSet
        schemaData={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Documentation",
          mainEntity: [
            ...(data?.map((item: any) => ({
              "@type": "Product",
              name: item.attributes.Title,
            })) ?? []),
          ],
        }}
      />
      <TealBox center maxWidth="544px">
        <h1 className="h3 ta-c">{helpData?.attributes.title}</h1>
        <FormTopicSearch locale={locale} />
      </TealBox>
      <div className="container container--small">
        <Breadcrumbs />
        <div className="text-boxes">
          <h6>
            <Trans>Browse by theme</Trans>
          </h6>
          <ul>
            {data?.map((item: any) => (
              <li key={item.id}>
                <Link
                  href={`/help-center/documentation/theme/${getSlug(
                    item.attributes
                  )}${
                    getFirstDocSublink(item.attributes.slug)
                      ? `/${getFirstDocSublink(item.attributes.slug)}`
                      : ""
                  }`}
                >
                  {item.attributes.Title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        {apps?.length > 0 && (
          <div className="text-boxes">
            <h6>
              <Trans>Browse by app</Trans>
            </h6>
            <ul>
              {apps?.map((item: any) => (
                <li key={item.id}>
                  <Link
                    href={`/help-center/documentation/app/${getSlug(
                      item.attributes
                    )}${
                      getFirstDocSublink(item.attributes.slug, "apps")
                        ? `/${getFirstDocSublink(item.attributes.slug, "apps")}`
                        : ""
                    }`}
                  >
                    {item.attributes.Title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const translation = await loadTranslation(
    ctx.locale!,
    process.env.NODE_ENV === "production"
  );
  const locale = getLocale(ctx.locale);

  const { data } = await fetchAPI("/themes", {
    populate: "*",
    locale,
  });


  const { data: appData } = await fetchAPI("/apps", {
    populate: "*",
    locale,
  });

  const { data: helpData } = await fetchAPI("/help-centre-landing", {
    populate: "*",
    locale,
  });


  const { data: docSEO } = await fetchAPI("/documentation-theme-listing", {
    populate: {
      SEO: {
        populate: "*",
      },
      localizations: {
        populate: "*",
      },
    },
    locale,
  });


  const { data: categories } = await fetchAPI("/categories", {
    populate: {
      documentations: {
        populate: {
          themes: "*",
        },
      },
    },
    sort: "order:asc",
    locale,
  });


  const props =  {
    translation,
    data,
    apps: appData,
    helpData,
    locales: handleBaseLocales(ctx.locales),
    docSEO,
    categories,
    locale: ctx.locale,
    localizationData: getLocalizationData(docSEO, "documentation"),
  };

  return {
    props,
    revalidate: 10,
  };
};

export default Documentation;
